import { lazy, type LazyExoticComponent, type ReactElement } from 'react';
import { useRouteAttempt } from '../lib/routeAttempt';

/**
 * The shape every console page has: a component that takes no props.
 *
 * The pages are exported **by name** (`export function DashboardPage()`), never as a default — their
 * own tests import them by name. `React.lazy` insists on a `default`, so the mapper below is what
 * bridges the two: one file (`app/routes.tsx`) instead of thirteen `export default` lines added to
 * thirteen pages.
 */
type PageComponent = () => ReactElement | null;

/**
 * The lazy objects, keyed by route id **and** attempt.
 *
 * The key is what makes the retry work: attempt 0 is one object per route, created on the first
 * visit and reused afterwards (a route that is already loaded does not suspend again when the
 * operator navigates back to it), while attempt 1 is a *different* object — a different
 * `import()`, a different promise, and therefore a different answer from the network.
 */
const cache = new Map<string, LazyExoticComponent<PageComponent>>();

/**
 * A page that is fetched as its own chunk.
 *
 * `id` is the navigation id of the page (`app/navigation.ts`) and is the cache key's first half;
 * `load` is the dynamic import, already mapped to a `default`.
 */
export function lazyPage(
  id: string,
  load: () => Promise<{ default: PageComponent }>,
): PageComponent {
  function LazyRoutePage(): ReactElement {
    const attempt = useRouteAttempt();
    const Component = lazyFor(`${id}#${attempt}`, load);
    return <Component />;
  }
  LazyRoutePage.displayName = `LazyRoutePage(${id})`;
  return LazyRoutePage;
}

function lazyFor(
  key: string,
  load: () => Promise<{ default: PageComponent }>,
): LazyExoticComponent<PageComponent> {
  const cached = cache.get(key);
  if (cached) return cached;
  const created = lazy(load);
  cache.set(key, created);
  return created;
}
